/* Theme custom file */
/**
 * ENG-3972: swallow the click that follows a drag on a product card carousel.
 *
 * The native slideshow does register a one-shot `click` canceller as soon as a
 * drag starts (assets/slideshow.js, `#handleMouseDown` -> `onPointerMove`), but
 * it registers it against the drag AbortController's signal, and `onPointerUp`
 * aborts that controller as its very first statement. Since the browser fires
 * `pointerup` before `click`, the canceller is torn down a beat too early: a
 * drag scrolls the carousel and then navigates to the PDP.
 *
 * Rather than forking slideshow.js -- which would freeze a 900-line core
 * component and break Horizon auto-updates for every surface using it, the PDP
 * gallery included -- this sits on top and drops the stray click. It guards
 * `.product-card-alt__media-link` only, the anchor wrapping the card carousel
 * (see snippets/product-card-alt-gallery.liquid), so nothing else is affected.
 *
 * Touch is unaffected: swiping scrolls through the browser's own scroll-snap,
 * not through this drag handler, and does not synthesise a click.
 */

/** Pointer travel, in px, above which a press counts as a drag, not a click. */
const DRAG_THRESHOLD = 8;

/** How long, in ms, a drag keeps swallowing the click that follows it. */
const SUPPRESS_WINDOW = 300;

const LINK_SELECTOR = '.product-card-alt__media-link';

/**
 * Timestamp until which the next click on a card carousel is swallowed.
 *
 * A time window rather than a one-shot listener on purpose: when a drag ends
 * outside the card, or on a pointer type that fires no click at all, a one-shot
 * listener would linger and eat the next legitimate click instead.
 */
let suppressUntil = 0;

document.addEventListener(
  'pointerdown',
  (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(LINK_SELECTOR)) return;

    const startX = event.clientX;
    const startY = event.clientY;

    document.addEventListener(
      'pointerup',
      (pointerUp) => {
        const travelled = Math.hypot(pointerUp.clientX - startX, pointerUp.clientY - startY);

        if (travelled < DRAG_THRESHOLD) return;

        suppressUntil = performance.now() + SUPPRESS_WINDOW;
      },
      { once: true, passive: true }
    );
  },
  { passive: true }
);

// Capture phase, so this runs before the anchor's default navigation.
document.addEventListener(
  'click',
  (event) => {
    if (performance.now() > suppressUntil) return;

    suppressUntil = 0;

    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(LINK_SELECTOR)) return;

    event.preventDefault();
    event.stopPropagation();
  },
  true
);
